import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from 'services/auth/authService';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';

interface ErrorMessages {
  email?: string;
  general?: string;
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const errors: ErrorMessages = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      errors.email = t('auth.registration.validation.emailRequired');
    } else if (!emailRegex.test(email)) {
      errors.email = t('auth.registration.validation.emailInvalid');
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRequestReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLocalLoading(true);
      const response: AxiosResponse = await authService.requestPasswordReset(email);

      if (response.status === 200) {
        navigate('/reset-password', { state: { email } });
      } else {
        throw new Error(response.data.message || 'Failed to send password reset OTP');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to send password reset OTP. Please try again.';
      setErrorMessages({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-geist transition-colors duration-300 ${
        isDark ? 'bg-fc-dark' : 'bg-fc-light'
      }`}
    >
      {localLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 ${isDark ? 'bg-[#0F172B]' : 'bg-white'}`}>
            <Spinner size="large" text={t('auth.forgotPassword.sendingOTP')} />
          </div>
        </div>
      )}

      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">
        {/* Logo */}
        <Logomark className="w-20 h-20" />

        {/* Title + Description */}
        <div className="w-full text-center space-y-4">
          <h1
            className={`text-2xl font-bold leading-8 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('auth.forgotPassword.title')}
          </h1>
          <p
            className={`text-base font-normal leading-6 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.forgotPassword.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleRequestReset} className="w-full space-y-4">
          {/* Email field */}
          <div className="space-y-1">
            <label
              htmlFor="email"
              className={`block text-base font-medium leading-6 ${
                isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
              }`}
            >
              {t('auth.forgotPassword.emailLabel')}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t('auth.forgotPassword.emailPlaceholder')}
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={{ '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties}
              className={`w-full px-3 py-2.5 text-base leading-6 rounded-fc outline-none transition-all ${
                isDark
                  ? 'bg-fc-dark-surface-input text-fc-dark-text-primary placeholder-fc-dark-text-muted fc-input-gradient-border-dark'
                  : 'bg-white text-fc-text-primary placeholder-fc-text-muted fc-input-gradient-border'
              }`}
            />
            {errorMessages.email && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errorMessages.email}</p>
            )}
          </div>

          {/* Error message */}
          {errorMessages.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded">
              <p className="text-sm text-red-700 dark:text-red-400">{errorMessages.general}</p>
            </div>
          )}

          {/* Submit Button - Gradient */}
          <button
            type="submit"
            disabled={localLoading}
            className="w-full h-12 bg-fc-gradient-btn text-white text-base font-medium leading-6 rounded-fc-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {localLoading ? (
              <>
                <Spinner size="small" color="white" />
                <span>{t('auth.forgotPassword.sending')}</span>
              </>
            ) : (
              t('auth.forgotPassword.sendButton')
            )}
          </button>
        </form>

        {/* Back to Login Link */}
        <p className="text-center">
          <span
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.forgotPassword.backTo')}{' '}
          </span>
          <Link
            to="/login"
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-[#51A2FF]' : 'text-fc-blue'
            } hover:underline`}
          >
            {t('auth.forgotPassword.loginLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
